import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Pagination } from '@/components/ui/Pagination'
import { Link, router, useForm } from '@inertiajs/react'
import { useState } from 'react'
import type { Pagination as PaginationType } from '@/types'
import {
  Edit,
  Award,
  FileText,
  MessageSquare,
  Heart,
  Trophy,
  TrendingUp,
  Clock,
  Star,
  Plus,
  UserCog
} from 'lucide-react'

interface UserDetail {
  id: number
  name: string
  pseudo: string
  first_name: string
  last_name: string
  email: string
  rpps: string
  specialty: string
  role: string
  community_id: number
  community_name: string | null
  total_points: number
  level: number
  internal_account: boolean
  created_at: string
  updated_at: string
}

interface UserStats {
  total_points: number
  level: number
  rank: number
  posts_count: number
  comments_count: number
  likes_received: number
  badges_count: number
}

interface PointTransaction {
  id: number
  action_type: string
  points: number
  source: string
  created_at: string
}

interface Badge {
  id: number
  name: string
  emoji: string
  awarded_at: string | null
}

interface ShowProps {
  user: UserDetail
  stats: UserStats
  point_history: PointTransaction[]
  point_history_pagination: PaginationType
  points_filter: 'all' | 'earned' | 'spent'
  badges: Badge[]
  roles: string[]
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short'
  })
}

function getRoleBadgeColor(role: string): string {
  switch (role) {
    case 'admin': return 'bg-yellow-100 text-yellow-800 border-yellow-300'
    case 'moderator': return 'bg-green-100 text-green-800 border-green-300'
    default: return 'bg-gray-100 text-gray-800 border-gray-300'
  }
}

function getRoleLabel(role: string): string {
  switch (role) {
    case 'admin': return 'Administrateur'
    case 'moderator': return 'Modérateur'
    default: return 'Membre'
  }
}

function getActionTypeLabel(actionType: string): string {
  const labels: Record<string, string> = {
    'create_post': 'Publication créée',
    'post_created': 'Publication créée',
    'create_comment': 'Commentaire ajouté',
    'receive_like': 'Like reçu',
    'like_received': 'Like reçu',
    'like_given': 'Like donné',
    'solution_accepted': 'Solution acceptée',
    'daily_visit': 'Visite quotidienne',
    'daily_reading': 'Lecture quotidienne',
    'admin_award': 'Attribution admin',
    'welcome': 'Bonus de bienvenue',
    'manual': 'Attribution manuelle',
    'formation': 'Formation suivie',
    'webinaire': 'Webinaire',
    'mentorat': 'Mentorat',
    'temoignage': 'Témoignage',
    'shop_purchase': 'Achat boutique',
    'quiz_correct_answer': 'Bonne réponse au quiz',
    'quiz_streak_bonus': 'Bonus streak quiz',
    'contest_submission': 'Participation concours photo',
    'contest_vote_received': 'Vote reçu sur photo',
    'contest_winner': 'Gagnant concours photo',
    'onboarding_tour': 'Tour d\'onboarding',
    'onboarding_step': 'Étape d\'onboarding',
    'onboarding_tasks_complete': 'Tâches d\'onboarding complétées',
    'onboarding_complete': 'Onboarding complété',
  }
  return labels[actionType] || actionType
}

function getSourceLabel(source: string): string {
  const labels: Record<string, string> = {
    'community': 'Communauté',
    'manual': 'Manuel',
    'shop': 'Boutique',
  }
  return labels[source] || source
}

function getSourceColor(source: string): string {
  switch (source) {
    case 'community': return 'bg-blue-100 text-blue-700'
    case 'manual': return 'bg-purple-100 text-purple-700'
    case 'shop': return 'bg-orange-100 text-orange-700'
    default: return 'bg-gray-100 text-gray-700'
  }
}

const STAT_CARDS = [
  { key: 'total_points', label: 'Points', icon: Star, color: 'bg-yellow-500' },
  { key: 'level', label: 'Niveau', icon: TrendingUp, color: 'bg-blue-500' },
  { key: 'rank', label: 'Classement', icon: Trophy, color: 'bg-purple-500' },
  { key: 'posts_count', label: 'Publications', icon: FileText, color: 'bg-green-500' },
  { key: 'comments_count', label: 'Commentaires', icon: MessageSquare, color: 'bg-pink-500' },
  { key: 'likes_received', label: 'Likes reçus', icon: Heart, color: 'bg-red-500' },
] as const

export default function Show({ user, stats, point_history, point_history_pagination, points_filter, badges, roles }: ShowProps) {
  const [showPointsModal, setShowPointsModal] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)

  const pointsForm = useForm({
    points: '',
    reason: ''
  })

  const roleForm = useForm({
    role: user.role
  })

  const handleAwardPoints = (e: React.FormEvent) => {
    e.preventDefault()
    pointsForm.post(`/admin/users/${user.id}/award_points`, {
      onSuccess: () => {
        setShowPointsModal(false)
        pointsForm.reset()
      }
    })
  }

  const handleChangeRole = (e: React.FormEvent) => {
    e.preventDefault()
    roleForm.post(`/admin/users/${user.id}/change_role`, {
      onSuccess: () => {
        setShowRoleModal(false)
      }
    })
  }

  return (
    <AdminLayout
      title={user.name}
      breadcrumbs={[
        { label: 'Utilisateurs', href: '/admin/users' },
        { label: user.name }
      ]}
    >
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-tessan-primary text-white flex items-center justify-center text-2xl font-bold">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h2 className="text-xl font-bold text-text-dark">{user.name}</h2>
            <p className="text-text-gray">{user.email}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRoleBadgeColor(user.role)}`}>
                {getRoleLabel(user.role)}
              </span>
              {user.specialty && (
                <span className="text-sm text-text-gray">{user.specialty}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowPointsModal(true)}
            className="flex items-center gap-2"
          >
            <Plus size={16} />
            Attribuer points
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowRoleModal(true)}
            className="flex items-center gap-2"
          >
            <UserCog size={16} />
            Changer rôle
          </Button>
          <Link href={`/admin/users/${user.id}/edit`}>
            <Button className="flex items-center gap-2">
              <Edit size={16} />
              Modifier
            </Button>
          </Link>
        </div>
      </div>

      {/* User Info */}
      <Card className="p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-text-gray">RPPS</p>
            <p className="font-medium text-text-dark">{user.rpps}</p>
          </div>
          <div>
            <p className="text-text-gray">Communauté</p>
            <p className="font-medium text-text-dark">{user.community_name || '-'}</p>
          </div>
          <div>
            <p className="text-text-gray">Inscription</p>
            <p className="font-medium text-text-dark">{formatDate(user.created_at)}</p>
          </div>
          <div>
            <p className="text-text-gray">Dernière modification</p>
            <p className="font-medium text-text-dark">{formatDate(user.updated_at)}</p>
          </div>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {STAT_CARDS.map(({ key, label, icon: Icon, color }) => (
          <Card key={key} className="p-4">
            <div className="flex items-center gap-3">
              <div className={`${color} p-2 rounded-lg text-white`}>
                <Icon size={18} />
              </div>
              <div>
                <p className="text-xl font-bold text-text-dark">
                  {key === 'rank' ? `#${stats[key]}` : stats[key].toLocaleString()}
                </p>
                <p className="text-xs text-text-gray">{label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Historique des points */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock size={20} className="text-tessan-primary" />
              <h3 className="text-lg font-display font-bold text-text-dark">
                Historique des points
              </h3>
              <span className="text-sm text-text-gray">
                ({point_history_pagination.total_count})
              </span>
            </div>
          </div>

          <div className="flex gap-1 mb-4">
            {([
              { value: 'all', label: 'Tout' },
              { value: 'earned', label: 'Reçus' },
              { value: 'spent', label: 'Dépensés' },
            ] as const).map(({ value, label }) => (
              <button
                key={value}
                onClick={() => {
                  router.get(
                    `/admin/users/${user.id}`,
                    { points_filter: value, activity_page: 1 },
                    { preserveState: true, preserveScroll: true }
                  )
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  points_filter === value
                    ? 'bg-tessan-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {point_history.length === 0 ? (
            <p className="text-text-gray text-center py-4">Aucune transaction</p>
          ) : (
            <>
              <div className="space-y-2">
                {point_history.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium text-text-dark text-sm">
                          {getActionTypeLabel(activity.action_type)}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${getSourceColor(activity.source)}`}>
                            {getSourceLabel(activity.source)}
                          </span>
                          <p className="text-xs text-text-gray">
                            {formatDate(activity.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <span className={`font-bold text-sm whitespace-nowrap ${activity.points >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {activity.points >= 0 ? '+' : ''}{activity.points} pts
                    </span>
                  </div>
                ))}
              </div>

              <Pagination
                pagination={point_history_pagination}
                itemLabel="transaction"
                onPageChange={(page) => {
                  router.get(
                    `/admin/users/${user.id}`,
                    { activity_page: page, points_filter },
                    { preserveState: true, preserveScroll: true }
                  )
                }}
              />
            </>
          )}
        </Card>

        {/* Badges */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Award size={20} className="text-tessan-primary" />
            <h3 className="text-lg font-display font-bold text-text-dark">
              Badges ({badges.length})
            </h3>
          </div>

          {badges.length === 0 ? (
            <p className="text-text-gray text-center py-4">Aucun badge obtenu</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {badges.map((badge) => (
                <div
                  key={badge.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <span className="text-2xl">{badge.emoji}</span>
                  <div>
                    <p className="font-medium text-text-dark text-sm">{badge.name}</p>
                    {badge.awarded_at && (
                      <p className="text-xs text-text-gray">
                        {formatShortDate(badge.awarded_at)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Award Points Modal */}
      {showPointsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6">
            <h3 className="text-lg font-display font-bold text-text-dark mb-4">
              Attribuer des points
            </h3>
            <form onSubmit={handleAwardPoints} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  Nombre de points
                </label>
                <Input
                  type="number"
                  min="1"
                  value={pointsForm.data.points}
                  onChange={(e) => pointsForm.setData('points', e.target.value)}
                  placeholder="Ex: 50"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  Raison (optionnel)
                </label>
                <Input
                  type="text"
                  value={pointsForm.data.reason}
                  onChange={(e) => pointsForm.setData('reason', e.target.value)}
                  placeholder="Ex: Contribution exceptionnelle"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowPointsModal(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={pointsForm.processing}>
                  Attribuer
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Change Role Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6">
            <h3 className="text-lg font-display font-bold text-text-dark mb-4">
              Changer le rôle
            </h3>
            <form onSubmit={handleChangeRole} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  Nouveau rôle
                </label>
                <Select
                  value={roleForm.data.role}
                  onChange={(e) => roleForm.setData('role', e.target.value)}
                >
                  {roles.map((role) => (
                    <option key={role} value={role}>
                      {getRoleLabel(role)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowRoleModal(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={roleForm.processing}>
                  Changer
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </AdminLayout>
  )
}
